import React, { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

/**
 * FECOA-1238: Amend the time windows of a LIVE schedule mid-study.
 *
 * Collects a required reason and posts the (already-edited) schedule config to
 * the versioned amend endpoint. The backend freezes every completed, in-progress,
 * or past window and regenerates only future windows, so historical completion
 * data is preserved by construction.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} scheduleId - the live Schedule UUID
 * @param {Object} config - the full new Schedule.config to apply
 * @param {(result: object) => void} [onAmended] - called with the amend summary
 */
const AmendScheduleModal = ({ open, onClose, scheduleId, config, onAmended }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required to amend a live schedule.');
      return;
    }
    setLoading(true);
    try {
      const result = await studyBuilderService.amendSchedule(scheduleId, config, reason.trim());
      toast.success(
        `Schedule amended (v${result.version_number}). ` +
        `${result.windows_frozen} historical window(s) preserved, ` +
        `${result.windows_created} future window(s) regenerated.`
      );
      onAmended?.(result);
      onClose();
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.config ||
        err?.response?.data?.detail ||
        'Failed to amend schedule.';
      toast.error(Array.isArray(detail) ? detail[0] : detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
            Amend live schedule
          </h2>
          <button
            onClick={onClose}
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="px-6 pb-5 pt-2">
          <p className="mb-4 text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-300">
            This schedule has active participants. Amending it applies the new timing
            to <strong className="font-semibold text-gray-900 dark:text-gray-50">future</strong> windows only. Completed, in-progress, and past
            windows and their responses are preserved unchanged.
          </p>

          <label className="label">
            Reason for amendment
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g. Sponsor protocol amendment v2, morning dose moved to 09:00"
            className="input"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            disabled={loading}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            className="btn btn-primary"
          >
            {loading ? 'Amending…' : 'Amend schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendScheduleModal;
